import type { QueryParams } from './api-options';

/**
 * Appends query parameters to a URL string.
 * Null and undefined values are filtered out. Returns the URL unchanged if no valid params exist.
 * Spaces are encoded as `%20` (not `+`).
 *
 * If the URL already contains query parameters, new params are appended with `&`.
 * Duplicate keys are not merged — both values will be present in the final URL.
 *
 * TODO: Add support for array values (e.g., `{ ids: [1, 2, 3] }`). Serialization
 * should be abstract — allow passing a custom key-value serializer so callers can
 * choose bracket notation (`ids[]=1&ids[]=2&ids[]=3`) for Rails, repeated keys
 * (`ids=1&ids=2&ids=3`) for other backends, or any other format.
 */
export function buildUrl(url: string, queries?: QueryParams): string {
  if (!queries) {
    return url;
  }

  const searchParams = new URLSearchParams();

  Object.entries(queries).forEach(([key, value]) => {
    if (value != null) {
      searchParams.append(key, String(value));
    }
  });

  // URLSearchParams encodes spaces as '+' (form-encoding); normalize to '%20' so
  // values decode to a space under RFC 3986 query parsing, which more backends honor.
  const queryString = searchParams.toString().replace(/\+/g, '%20');
  if (!queryString) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${queryString}`;
}
